import { Injectable, ElementRef, Inject } from '@angular/core';
import {
  AnimationBuilder,
  style,
  animate,
  keyframes,
  AnimationFactory,
  AnimationPlayer,
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';

export type AnimationValueDelegate = (
  el?: HTMLElement,
  index?: number,
  list?: HTMLElement[]
) => number | string;

export interface AnimationResult {
  element: HTMLElement;
  animation: AnimationPlayer;
}

export interface AnimationOptions {
  targets: HTMLElement | string | ElementRef | HTMLElement[] | NodeList;

  fillMode?: 'forwards' | 'none' | null;
  easing?: string | AnimationValueDelegate;
  duration?: number | string | AnimationValueDelegate;
  delay?: number | string | AnimationValueDelegate;
  [prop: string]: any;
  height: number;
}

export interface Animator {
  animate(options: any): Promise<any>;
}
@Injectable({
  providedIn: 'root',
})
export class AnimationService implements Animator {
  constructor(
    private _animationBuilder: AnimationBuilder,
    @Inject(DOCUMENT) private _document: any
  ) {}

  animate(options: AnimationOptions | any) {
    const elements = this._getAnimationTargetNodes(options.targets);

    const animationBuilders = elements.map((el, index, ar) => {
      const animFactory = this._buildAnimation(el, index, options, ar);
      return { factory: animFactory, element: el };
    });

    const promises = animationBuilders.map(ab => {
      return this._playAnimationsAndGetPromises(
        ab.element,
        ab.factory,
        options
      );
    });

    return Promise.all(promises);
  }

  private _getAnimationTargetNodes(
    targets: HTMLElement | string | ElementRef | HTMLElement[] | NodeList
  ): HTMLElement[] {
    // Convert a NodeList into an Array
    const parseNodeList = (nl: NodeList) => Array.prototype.slice.call(nl);

    if (typeof targets === 'string') {
      return parseNodeList(this._document.querySelectorAll(targets));
    }
    if (targets instanceof ElementRef) {
      return [targets.nativeElement];
    }
    if (targets instanceof HTMLElement) {
      return [targets];
    }
    if (targets instanceof NodeList) {
      return parseNodeList(targets);
    }

    // Fall al the way through to the HTMLElement[]
    // and just return that since that is our target type.
    return targets;
  }

  private _buildAnimation(
    el: HTMLElement,
    index: number,
    animationOptions: AnimationOptions,
    elementArray: HTMLElement[]
  ) {
    let useFromStyle = false;
    const fromStyle = {};
    const toStyle = {};

    let { delay, duration, easing } = animationOptions;

    if (typeof duration === 'function') {
      duration = duration(el, index, elementArray);
    }
    if (typeof duration === 'number') {
      duration = `${duration || 0}ms`;
    }

    if (typeof delay === 'function') {
      delay = delay(el, index, elementArray);
    }
    if (typeof delay === 'number') {
      delay = `${delay || 0}ms`;
    }

    if (!delay) {
      delay = '0ms';
    }

    if (typeof easing === 'function') {
      easing = easing(el, index, elementArray).toString();
    }

    for (const prop in animationOptions) {
      // Ignore the animation control props
      if (
        prop === 'targets' ||
        prop === 'easing' ||
        prop === 'duration' ||
        prop === 'delay' ||
        prop === 'fillMode'
      ) {
        continue;
      }

      const animProp = animationOptions[prop];
      if (Array.isArray(animProp)) {
        if (animProp.length < 2) {
          throw new Error('invalid initial property array');
        }
        useFromStyle = true;
        fromStyle[prop] = animProp[0];
        toStyle[prop] = animProp[1];
      } else {
        toStyle[prop] = animProp;
      }
    }

    const keyFrames = [style(toStyle)];
    if (useFromStyle) {
      keyFrames.unshift(style(fromStyle));
    }
    const animationFactory = this._animationBuilder.build([
      animate(
        `${duration} ${delay} ${easing || 'linear'}`,
        keyframes(keyFrames)
      ),
    ]);

    return animationFactory;
  }

  private _playAnimationsAndGetPromises(
    element: HTMLElement,
    factory: AnimationFactory,
    opts: AnimationOptions
  ): Promise<AnimationResult> {
    const animation = factory.create(element);
    const promise = new Promise<{
      element: HTMLElement;
      animation: AnimationPlayer;
    }>(resolve => {
      animation.onDone(() =>
        resolve({
          animation: animation,
          element: element,
        })
      );
    });

    if (opts.fillMode !== 'forwards') {
      promise.then((data: any) => {
        data.animation.destroy();
        return data;
      });
    }
    animation.play();
    return promise;
  }
}
